import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/SEO";
import { company } from "@/data/company";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().min(1, "Please enter your name").max(120),
  email: z.string().email("Please enter a valid email"),
  subject: z.string().min(1, "Please enter a subject").max(200),
  message: z.string().min(10, "Message should be at least 10 characters").max(5000),
});

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/contact`, { ...data, category: "general" });
      toast.success("Message sent! We'll get back to you shortly.");
      setSubmitted(true);
      reset();
    } catch (e) {
      toast.error("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with Envago Labs. Send us a message and we'll respond as soon as possible."
        path="/contact"
      />

      <section className="relative overflow-hidden gradient-emerald">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-36 sm:px-8 md:pt-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Contact
            </span>
            <h1 className="mt-5 font-display text-5xl font-light leading-none tracking-tighter md:text-6xl">
              Let's talk.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Questions, feedback or partnership ideas — fill out the form and we'll be in touch.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-24 sm:px-8">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Info */}
          <div className="md:col-span-4">
            <div className="rounded-2xl border border-border bg-card p-7">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <h3 className="mt-5 font-display text-lg font-medium tracking-tight">Email us directly</h3>
              <div className="mt-4 space-y-3">
                <a
                  href={`mailto:${company.emails.company}`}
                  className="block text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {company.emails.company}
                </a>
                <a
                  href={`mailto:${company.emails.tanzeen}`}
                  className="block text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {company.emails.tanzeen}
                </a>
              </div>
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                We typically respond within a couple of business days.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-8">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="flex h-full flex-col items-center justify-center rounded-2xl border border-border bg-card p-12 text-center"
              >
                <div className="grid h-14 w-14 place-items-center rounded-full bg-primary/10">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <h3 className="mt-5 font-display text-2xl font-medium tracking-tight">
                  Thank you!
                </h3>
                <p className="mt-2 max-w-md text-muted-foreground">
                  Your message has been received. We'll get back to you as soon as possible.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="mt-6 rounded-full"
                  data-testid="contact-send-another"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="rounded-2xl border border-border bg-card p-7 md:p-9"
                data-testid="contact-form"
                noValidate
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      {...register("name")}
                      placeholder="Your name"
                      className="mt-2"
                      data-testid="contact-input-name"
                    />
                    {errors.name && (
                      <p className="mt-1.5 text-xs text-destructive" data-testid="error-name">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register("email")}
                      placeholder="you@example.com"
                      className="mt-2"
                      data-testid="contact-input-email"
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-xs text-destructive" data-testid="error-email">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    {...register("subject")}
                    placeholder="What's this about?"
                    className="mt-2"
                    data-testid="contact-input-subject"
                  />
                  {errors.subject && (
                    <p className="mt-1.5 text-xs text-destructive" data-testid="error-subject">
                      {errors.subject.message}
                    </p>
                  )}
                </div>

                <div className="mt-5">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    rows={6}
                    {...register("message")}
                    placeholder="Tell us how we can help..."
                    className="mt-2 resize-none"
                    data-testid="contact-input-message"
                  />
                  {errors.message && (
                    <p className="mt-1.5 text-xs text-destructive" data-testid="error-message">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="mt-7 rounded-full px-7"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? "Sending..." : "Send message"}
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
